/* eslint-disable react/display-name */
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Cell } from 'react-table';
import _ from 'lodash';
import { AssetSymbol, AssetType } from '@tradingblock/types';
import { isIndexSymbol } from '@tradingblock/api';
import { EmptyBlockContent, Loading, Number, SignedNumber } from '@tradingblock/components';
import {
  useStateSelector,
  getFavorites,
  getFavoriteSymbols,
  dashboardIdSelector,
  blocksSelectors,
} from '../../data/global/dataSelectors';
import { getQuotesBySymbols } from '../../data/global/selectors/quoteSelector';
import { useDispatcher } from '../../data/global/hooks';
import { DirectionalPrice } from '../../components/trading/DirectionalPrice';
import { useBlockGroupInfo } from '../useGroupState';
import { changeCalculation } from '../../utilities/position';
import { useFavoriteSettings } from './useFavoriteSettings';
import { BlockTable } from '../shared/BlockTable';
import { BlockAction, useBlockMetadata } from '../../components/blocks/BlockState';
import { useBlockTable } from '../shared/table/useBlockTableActions';
import { SymbolCell, TradeCell, ChartCell } from './FavoritesCells';

export interface FavoriteRowProps {
  AssetSymbol: AssetSymbol;
  Symbol: string;
  LastTradePrice: number;
  NetChange: number;
  ChangePercentage: number;
  BidPrice: number;
  AskPrice: number;
  Open: number;
  High: number;
  Low: number;
  ClosePrice: number;
  ClosePriceTMinus2: number;
  IsSettlementSet: boolean;
  CalculatedNetChange: number;
  CalculatedChangePercent: number;
}

const DefaultQuoteData = {
  Symbol: '',
  LastTradePrice: 0,
  NetChange: 0,
  ChangePercentage: 0,
  BidPrice: 0,
  AskPrice: 0,
  Open: 0,
  High: 0,
  Low: 0,
  ClosePrice: 0,
  ClosePriceTMinus2: 0,
  IsSettlementSet: false,
  CalculatedNetChange: 0,
  CalculatedChangePercent: 0,
};

export const FavoritesContent: React.FunctionComponent<{}> = () => {
  const { state } = useBlockTable('favorites');
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);
  const { groupId } = useBlockGroupInfo();
  const { favorites, symbols } = useStateSelector(s => ({
    favorites: getFavorites(s),
    symbols: getFavoriteSymbols(s),
  }));
  const [pageIndex, setPageIndex] = useState(state.pageIndex);
  const [pageSize, setPageSize] = useState(state.pageSize);
  const total = _.size(favorites);

  const { dispatcher } = useDispatcher();
  const remove = useCallback(
    (symbol: string) => {
      dispatcher.favorites.remove(symbol);
    },
    [dispatcher.favorites.remove]
  );

  const quoteMetaData = useStateSelector(s => getQuotesBySymbols(s, symbols));

  const data = useMemo(() => {
    const rawData = _.filter(
      _.map(favorites, f => {
        const quoteData = _.find(quoteMetaData, q => q.Symbol === f.symbol) || DefaultQuoteData;
        const {
          Symbol,
          LastTradePrice,
          NetChange,
          ChangePercentage,
          ClosePrice,
          Open,
          High,
          Low,
          BidPrice,
          AskPrice,
          IsSettlementSet,
          ClosePriceTMinus2,
        } = quoteData;

        const { change, changePercent } = changeCalculation(
          LastTradePrice,
          ClosePrice,
          ClosePriceTMinus2,
          BidPrice,
          AskPrice,
          Symbol,
          IsSettlementSet
        );
        return {
          Symbol,
          AssetSymbol: f,
          LastTradePrice,
          NetChange,
          ChangePercentage,
          BidPrice,
          AskPrice,
          Open,
          High,
          Low,
          ClosePrice,
          ClosePriceTMinus2,
          IsSettlementSet,
          CalculatedNetChange: change,
          CalculatedChangePercent: changePercent * 100,
        };
      }),
      d => !!d.Symbol
    );

    if (sortBy) return sortByKey(rawData, sortBy, sortDirection === 'desc');
    return rawData;
  }, [quoteMetaData, sortBy, sortDirection]);

  const getRowKey = useCallback((data: FavoriteRowProps) => `${data.Symbol}`, []);

  const [tableColumnOrder, setTableColumnOrder] = useState<string[]>([]);

  const onSort = useCallback(
    (sort: any[]) => {
      const sortVal = _.first(sort);
      if (sortVal) {
        const { id, desc, asc } = sortVal;
        setSortBy(id);
        setSortDirection(desc ? 'desc' : asc ? 'asc' : undefined);
      } else {
        setSortBy(undefined);
        setSortDirection(undefined);
      }
    },
    [tableColumnOrder]
  );

  const getColumnOrder = (columns: string[]) => {
    return setTableColumnOrder(columns);
  };

  const { blockId } = useBlockMetadata();
  const dashboardId = useStateSelector(dashboardIdSelector);
  const { type } = useBlockMetadata();
  const { dispatch } = useDispatcher();
  const blockType = type;
  const blocksLoading = useStateSelector(s => s.blocks.isFetching);
  const isLinked = useStateSelector(s => blocksSelectors.isLinked(s)(blockId));
  const allBlocks = useStateSelector(s => blocksSelectors.blocks(s));

  useEffect(() => {
    const settingsSaveData = {
      dashboardId: dashboardId || '',
      blockId,
      blockType,
      blockSettings: {
        ...current,
        sortBy: sortBy,
        sortDirection: sortDirection,
      },
    };
    dispatch(BlockAction.setSettings(settingsSaveData.blockSettings));
    dispatcher.settings.block.setBlockSetting(settingsSaveData);
    return () => {
      dispatch(BlockAction.setSettings(settingsSaveData.blockSettings));
      dispatcher.settings.block.setBlockSetting(settingsSaveData);
    };
  }, [tableColumnOrder, sortBy, sortDirection]);

  const pageCount = useMemo(() => {
    if (pageSize) {
      return _.ceil((total || 0) / pageSize) === 0 ? 1 : _.ceil((total || 0) / pageSize);
    }
    return 0;
  }, [total, pageSize]);

  const onPage = useCallback((pageIndex: number) => {
    setPageIndex(pageIndex);
  }, []);

  const onPageSize = useCallback((pageSize: number) => {
    setPageSize(pageSize);
  }, []);

  const columns: any[] = useMemo(
    () => [
      {
        Header: 'Symbol',
        accessor: 'Symbol',
        canSort: true,
        Cell: ({ row }: Cell<FavoriteRowProps>) => (
          <SymbolCell
            row={row}
            isLinked={isLinked}
            groupId={groupId}
            allBlocks={allBlocks}
            dispatcher={dispatcher}
          />
        ),
      },
      {
        Header: 'Last',
        accessor: 'LastTradePrice',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => <DirectionalPrice value={row.original.LastTradePrice} />,
      },
      {
        Header: 'Change',
        accessor: 'NetChange',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => {
          const val = useRef(0.0);
          if (row.original.NetChange) val.current = row.original.NetChange;
          return <SignedNumber value={val.current} bold currency roundDecimal={3} />;
        },
      },
      {
        Header: '(%)',
        accessor: (a: FavoriteRowProps) => a.ChangePercentage,
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => {
          const val = useRef(0.0);
          if (row.original.ChangePercentage) val.current = row.original.ChangePercentage;
          return <SignedNumber value={val.current} bold percent hideIcon />;
        },
      },
      {
        Header: 'Bid',
        accessor: 'BidPrice',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => {
          return isIndexSymbol(row.original.Symbol) ? '-' : <DirectionalPrice value={row.original.BidPrice} />;
        },
      },
      {
        Header: 'Ask',
        accessor: 'AskPrice',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => {
          return isIndexSymbol(row.original.Symbol) ? '-' : <DirectionalPrice value={row.original.AskPrice} />;
        },
      },
      {
        Header: 'Open',
        accessor: 'Open',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => {
          const isOption = row.original.AssetSymbol.symbol.split(' ').length > 1;
          const isIndex = row.original.AssetSymbol.assetType === AssetType.Index;
          if (isOption || isIndex) return '-';
          return <Number value={row.original.Open} currency />;
        },
      },
      {
        Header: 'Prev. Close',
        accessor: 'ClosePrice',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => {
          return (
            <Number
              value={row.original.IsSettlementSet ? row.original.ClosePriceTMinus2 : row.original.ClosePrice}
              currency
            />
          );
        },
      },
      {
        Header: 'Close',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => {
          if (row.original.IsSettlementSet) {
            return <Number value={row.original.ClosePrice} currency />;
          } else {
            return <i className="fal fa-clock fa-lg mute" title="Settlement price not yet available." />;
          }
        },
      },
      {
        Header: 'High',
        accessor: 'High',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => <Number value={row.original.High} currency />,
      },
      {
        Header: 'Low',
        accessor: 'Low',
        canSort: true,
        sortType: 'alphanumeric',
        Cell: ({ row }: Cell<FavoriteRowProps>) => <Number value={row.original.Low} currency />,
      },
      {
        Header: 'Trade',
        id: 'trade',
        Cell: ({ row }: Cell<FavoriteRowProps>) => <TradeCell row={row} groupId={groupId} />,
      },
      {
        Header: 'Chart',
        id: 'chart',
        Cell: ({ row }: Cell<FavoriteRowProps>) => (
          <ChartCell row={row} groupId={groupId} dispatcher={dispatcher} />
        ),
      },
      {
        Header: 'Remove',
        id: 'favorites-last-col',
        className: 'col-delete',
        Cell: ({ row }: Cell<FavoriteRowProps>) => (
          <button
            className="btn btn-blend remove-btn"
            title="Remove"
            onClick={() => {
              const symbolToRemove =
                (row.original.AssetSymbol && row.original.AssetSymbol.symbol) || row.original.Symbol;
              if (symbolToRemove) remove(symbolToRemove);
            }}
            type="button"
          >
            <i className="fas fa-times-circle" />
          </button>
        ),
      },
    ],
    [remove, groupId, dispatcher, isLinked, allBlocks, tableColumnOrder]
  );

  const { filterHiddenColumns, current } = useFavoriteSettings();
  const filteredColumns = useMemo(
    () => filterHiddenColumns(columns, current),
    [columns, current, filterHiddenColumns]
  );

  //Set sortBy and sort Direction
  useEffect(() => {
    if (current) {
      if (current.sortBy) setSortBy(current.sortBy);
      if (current.sortDirection) setSortDirection(current.sortDirection);
    }
  }, []);

  const initialState = useMemo(() => {
    return {
      pageSize: _.isUndefined(pageSize) ? 50 : pageSize,
      pageIndex: pageIndex || 0,
      sortBy: sortBy ? [{ id: sortBy, desc: sortDirection === 'desc' }] : [],
      columnOrder: [],
    };
  }, [pageSize, pageIndex, total, sortBy, sortDirection]);

  // we need to chunk the data based on the current page size and page index
  const chunkedData = useMemo(() => {
    if (pageSize === 0) {
      return data;
    }
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }, [data, pageIndex, pageSize]);

  function sortByKey(arr: any[], key: string, desc: boolean = true) {
    return arr.toSorted((a, b) => {
      const valA = a[key];
      const valB = b[key];

      let result = 0;

      if (typeof valA === 'string' && typeof valB === 'string') {
        result = valA.localeCompare(valB);
      } else {
        result = (valA as any) - (valB as any);
      }

      return desc ? -result : result;
    });
  }

  return (
    <>
      {data && data.length > 0 && !blocksLoading && (
        <BlockTable
          tableKey="favorites"
          columns={filteredColumns}
          data={chunkedData}
          getRowKey={getRowKey}
          sortable
          frozenHeaders
          hasPagination
          manualPagination
          onSort={onSort}
          pageCount={pageCount}
          onPage={onPage}
          initialState={initialState}
          onPageSize={onPageSize}
          numColumns={_.size(columns)}
          getColumnOrder={getColumnOrder}
          loaded={true}
        />
      )}
      {!data || (data.length === 0 && <EmptyBlockContent>You have no favorites.</EmptyBlockContent>)}
      {blocksLoading && <Loading />}
    </>
  );
};
